import { ACTIVE_VERSION } from '../../constants' // Key used to persist the assigned version in localStorage
import { abTests, testType, lastFallbackVersion } from 'helpers/ab/tests' // Import the A/B test configuration file

// Define type for version ID (integer)
export type VersionID = number

// Get today's date in "YYYY-MM-DD" format
const today = new Date().toISOString().slice(0, 10)

/**
 * Get previously assigned version from localStorage.
 * Returns null if not assigned yet.
 */
export const getStoredVersion = (): VersionID | null => {
  const stored = localStorage.getItem(ACTIVE_VERSION)
  return stored ? parseInt(stored, 10) : null
}

/**
 * Store assigned version to localStorage for future reuse.
 */
export const storeVersion = (version: VersionID): void => {
  localStorage.setItem(ACTIVE_VERSION, version.toString())
}

/**
 * Randomly draw a version based on allocation weights.
 *
 * Example:
 *  versions = [1, 2, 3]
 *  weights  = [0.5, 0.3, 0.2]
 *
 * Random number (0.0- 1.0) being compared with the weight intervals ranges:
 *  [0.0 - 0.5) → version 1 if rand < 0.5
 *  [0.5 - 0.8) → version 2 if rand >= 0.5 and < 0.8
 *  [0.8 - 1.0) → version 3 if rand >= 0.8
 */
export const drawVersion = (
  versions: VersionID[],
  weights: number[]
): VersionID => {
  const rand = Math.random() // Random number between 0 and 1
  let cumulative = 0         // Running total of weight ranges

  for (let i = 0; i < versions.length; i++) {
    cumulative += weights[i] // Expand the weight range as we iterate

    // If rand falls within this range, select this version
    if (rand <= cumulative) {
      return versions[i]
    }

    // Example:
    // rand = 0.72
    // cumulative after i=0: 0.5 → skip
    // cumulative after i=1: 0.8 → rand <= 0.8 → return version[1]
  }

  // Return last fallback version (shouldn’t happen unless weights are incorrect)
  return lastFallbackVersion
}

/**
 * Check if a test is currently active (today is between start and end dates | after start and no end date).
 */
const isDateInRange = (start: string | null, end: string | null): boolean => {
    if (!start) return false // No start date means not active
    if (today >= start && end === null) return true // Active until indefinite end
    if (today >= start && end !== null && today <= end) return true // Active until end date
    return false // Fallback not active
}

/**
 * Main function: get or draw a version from the running A/B test, for current user.
 * 
 * Logic:
 *  - If already assigned (localStorage), return it
 *  - Else Pick the active test (first one that matches today’s date)
 *  - If none, use fallback (test with null dates)
 *  - Use `drawVersion()` to select based on allocation
 *  - Store and return the assigned version
 */
export const getOrDarwVersion = async (): Promise<VersionID> => {
  const existing = getStoredVersion()
  if (existing !== null) return existing

  // Try to find an active test by date
  const activeTest = abTests.find((t: testType) =>
    isDateInRange(t.start_date, t.end_date)
  )
  // Fallback - Set defualt test (test 0) if no active test was found (1 version with 100% allocation)
  const fallbackTest = abTests.find((t: testType) =>
    t.test_id === 0
  )
  // Use the active test if available, otherwise fallback to the default test
  const selectedTest = activeTest || fallbackTest

  // Fallback on falback - Extremely unlikely will simply return version (x) and exit
  // This is a safety net in case no tests are valid include the fallback test
  if (!selectedTest) {
    return lastFallbackVersion
  }

  const { active_versions, allocation } = selectedTest

  // Safety check: arrays (allocation and versions) must align
  if (
    !active_versions ||
    !allocation ||
    active_versions.length !== allocation.length
  ) {
    return lastFallbackVersion  // Last fallback if allocation is invalid
  }

  const version_draw_result = drawVersion(active_versions, allocation)
  storeVersion(version_draw_result)
  return version_draw_result
}